import React, { useState, useEffect } from 'react'
import { usePost } from '../../../components/component-items/helpers'
import { globalState } from '../../../store'
import styled from 'styled-components'

// Components
import GlobalStyles from '../../../components/component-items/styles'
import { OrderInfo } from './styles'
import { Form, Button, Spinner } from 'react-bootstrap'
import { HoverNotes } from '../../../components/component-items/hover-notes'
import { StatusBoxStyled } from '../../../components/component-items/status-box'
import { usePermissions } from '../../../hooks/usePermissions'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCcMastercard, faCcVisa } from '@fortawesome/free-brands-svg-icons'
import { faCreditCard, faMoneyCheckAlt } from '@fortawesome/pro-duotone-svg-icons'

import { RechargeSettingsProps } from './types'

const Label = styled(Form.Label)`
  margin-top: 1em;
`

const SaveButton = styled(Button)`
  && {
    min-width: unset;
    width: auto;
    max-width: 80px;
    padding-left: 12px;
    padding-right: 12px;
  }
`

export const RechargeSettings = ({
  rechargeAmount,
  rechargeThreshold,
  maxRechargeAmount,
  primary,
  secondary,
  loaded = true,
}: RechargeSettingsProps) => {
  const [amount, setAmount] = useState('250')
  const [savedAmount, setSavedAmount] = useState('250')
  const [threshold, setThreshold] = useState('0')
  const [savedThreshold, setSavedThreshold] = useState('0')
  const amountOptions = [25, 50, 100, 250, 500, 1000, 2000, 5000, 10000, 25000, 50000, 100000]
  const {
    state: { csrf },
  } = globalState()
  const { hasAccess, isWarehouseUser } = usePermissions()
  const canEditRecharge = hasAccess('billing', 'admin')
  // The recharge threshold may only be edited by warehouse users; merchants
  // (customers) get a read-only view of the current value.
  const canEditThreshold = isWarehouseUser && canEditRecharge

  useEffect(() => {
    // Values may arrive as numbers from the API, so normalize to strings.
    const amountStr = String(rechargeAmount ?? '250')
    const thresholdStr = String(rechargeThreshold ?? '0')
    setAmount(amountStr)
    setSavedAmount(amountStr)
    setThreshold(thresholdStr)
    setSavedThreshold(thresholdStr)
  }, [rechargeAmount, rechargeThreshold])

  const normalizedThreshold = String(threshold).trim()
  const thresholdNumeric = Number(normalizedThreshold)
  // The input is whole-dollar only; reject decimals, exponent/Infinity forms so
  // we never POST an unexpected value to the backend.
  const isThresholdValid = normalizedThreshold !== '' && Number.isInteger(thresholdNumeric)
  // Only surface the Save action once a value has actually changed from what is
  // persisted (and, for the threshold, is a valid number).
  const isThresholdDirty = canEditThreshold && isThresholdValid && thresholdNumeric !== Number(savedThreshold)
  const isAmountDirty = canEditRecharge && Number(amount) !== Number(savedAmount)
  const isDirty = isThresholdDirty || isAmountDirty

  // Match the backend cap (ACH vs credit card) so we never offer an amount the
  // server would silently reduce. Always include the current value so the
  // selection never goes blank (e.g. if the payment method changed).
  const amountCap = maxRechargeAmount ?? Infinity
  const availableAmountOptions = Array.from(
    new Set([...amountOptions.filter((a) => a <= amountCap), Number(amount)])
  ).sort((a, b) => a - b)

  const handleSave = async () => {
    const payload: Record<string, string> = {}
    if (isAmountDirty) payload.rechargeAmount = amount
    if (isThresholdDirty) payload.rechargeThreshold = String(thresholdNumeric)
    if (Object.keys(payload).length === 0) return
    const res: any = await usePost(`/api/billing/details/`, payload, csrf)
    if (res?.success) {
      setSavedAmount(amount)
      setSavedThreshold(String(thresholdNumeric))
    }
  }

  return (
    <OrderInfo style={{ display: 'block', height: 'unset' }}>
      <GlobalStyles.CardHeader style={{ padding: '0 12px' }}>
        <GlobalStyles.CardTitle>
          <h3>Recharge Settings</h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          {isDirty && (
            <SaveButton variant="primary" onClick={handleSave}>
              Save
            </SaveButton>
          )}
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>
      <div>
        {!loaded ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3em' }}>
            <Spinner animation="border" role="status" variant="secondary">
              <span className="visually-hidden">Loading…</span>
            </Spinner>
          </div>
        ) : (
          <Form.Group style={{ margin: '1em' }}>
            <Label>
              <HoverNotes
                description="When your balance drops below this amount, we will automatically charge your primary payment method by the recharge amount."
                title="Recharge Threshold"
              />
            </Label>
            <Form.Control
              type="number"
              step="100"
              value={threshold}
              disabled={!canEditThreshold}
              aria-label="Recharge threshold"
              placeholder="Enter threshold (negatives allowed)"
              onChange={(e) => setThreshold(e.target.value)}
            />
            <Label>Recharge Amount</Label>
            <Form.Select
              value={amount}
              disabled={!canEditRecharge}
              aria-label="Recharge amount"
              onChange={(e) => setAmount(e.target.value)}
            >
              {availableAmountOptions.map((a) => (
                <option value={a} key={a}>{`$${a.toLocaleString()}`}</option>
              ))}
            </Form.Select>
            <StatusBoxStyled className={'yellow'} style={{ width: '100%', marginTop: '12px' }}>
              NOTE: You can recharge up to $5,000 with a credit card or $25,000 via ACH.
            </StatusBoxStyled>
            <Label>Primary Method</Label>
            <div style={{ color: 'black', padding: '0 1em' }}>
              <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}>
                <FontAwesomeIcon
                  icon={
                    primary.cardType === 'MC'
                      ? faCcMastercard
                      : primary.cardType === 'VI'
                        ? faCcVisa
                        : primary.ach
                          ? faMoneyCheckAlt
                          : faCreditCard
                  }
                  style={{ fontSize: '1.5em', marginRight: 5 }}
                />
                {primary.lastDigits ? primary.lastDigits : 'None'}
              </div>
              {primary.ach ? (
                <div>ACH</div>
              ) : (
                <>
                  <div>{primary.name}</div>
                  <div>{primary.expiry}</div>
                </>
              )}
            </div>
            <Label>Backup Method</Label>
            <div style={{ color: 'black', padding: '0 1em' }}>
              <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}>
                <FontAwesomeIcon
                  icon={
                    secondary.cardType === 'MC'
                      ? faCcMastercard
                      : secondary.cardType === 'VI'
                        ? faCcVisa
                        : secondary.ach
                          ? faMoneyCheckAlt
                          : faCreditCard
                  }
                  style={{ fontSize: '1.5em', marginRight: 5 }}
                />
                {secondary.lastDigits ? secondary.lastDigits : 'None'}
              </div>
              {secondary.ach ? (
                <div>ACH</div>
              ) : (
                <>
                  <div>{secondary.name}</div>
                  <div>{secondary.expiry}</div>
                </>
              )}
            </div>
          </Form.Group>
        )}
      </div>
    </OrderInfo>
  )
}
